import { WorksheetDomain } from '@mathflat/domain/@entities'
import { Entity as ProblemEntity } from '@mathflat/domain/@entities/Problem/dto'
import { debounce } from 'lodash-es'
import { observer } from 'mobx-react'
import { useCallback, useEffect, useRef } from 'react'
import { useNavigate, useParams } from 'react-router'

import { type AutoScoringRequest, RequestScoring } from '~/@common/api/request/RequestScoring'
import { routeName } from '~/@common/constants'
import { useLearningTime } from '~/@common/hooks/useLearningTime'
import {
  resolveViewerMode,
  WORKSHEET_WRITE_KEY,
} from '~/@common/models/(Permission)/permissionUtils'
import { useStudentAppMediaQuery } from '~/@common/hooks/useMediaQuery'
import { useRepository } from '~/@common/hooks/useRepository'
import { learningContentLastScreenService } from '~/@common/services/learningContentLastScreen/LearningContentLastScreen.service'
import modalService from '~/@common/services/modal.service'
import { commonRepo } from '~/@common/services/repo.service'
import { mixpanelClient } from '~/@common/utils/trackings/mixpanel'
import WorksheetScoringByOne from '~/@pages/@widgets/(Worksheet)/WorksheetScoring/WorksheetScoringByOne'
import WorksheetScoringByOneMobile from '~/@pages/@widgets/(Worksheet)/WorksheetScoring/WorksheetScoringByOne.mobile'
import { openUnavailableAlertMessageModal } from '~/@pages/@widgets/(Worksheet)/접근권한모달'
import { ScoringSubmitConfirmModal } from '~/@pages/student/@widgets/StudentWorksheetScoring/ScoringSubmitConfirmModal'

import { StudentWorksheetDetailPageService } from './@service/StudentWorksheetDetailPage.service'
import { StudentWorksheetHeader } from './@widgets/StudentWorksheetHeader'
import { alertWorksheetExceptionErrorModal } from './scoring/alertWorksheetExceptionErrorModal'

const StudentWorksheetDetailPage = observer(() => {
  const { studentWorksheetId } = useParams<{ studentWorksheetId: string }>()
  // TODO: /student-worksheet 나갈 때 dynamicRepo.del 시킬 수 있도록, student-worksheet을 감싸는 페이지 컴포넌트 신설 필요
  const service = useRepository([StudentWorksheetDetailPageService, studentWorksheetId!])

  // 학부모는 한문제씩 페이지(WorksheetDetail) 자체에 진입 불가 — 빠른채점 페이지로 강제 redirect.
  const isParentView = resolveViewerMode(commonRepo.permission, WORKSHEET_WRITE_KEY) === 'parent'

  useLearningTime(service.worksheet?.type, service.studentWorksheet?.status, !isParentView)

  const navigate = useNavigate()

  useEffect(() => {
    if (isParentView && studentWorksheetId) {
      navigate(
        routeName.student.studentWorksheetScoringById.replace(
          ':studentWorksheetId',
          studentWorksheetId,
        ),
        { replace: true },
      )
    }
  }, [isParentView, studentWorksheetId])
  const tempLocalUserInputRef = useRef<
    Map<
      AutoScoringRequest<'WORKSHEET', '일반'>['worksheetProblemId'],
      AutoScoringRequest<'WORKSHEET', '일반'>
    >
  >(new Map())

  const { isMobile } = useStudentAppMediaQuery()

  useEffect(() => {
    // 학부모는 빠른채점 페이지로 redirect 되므로
    if (isParentView) return
    if (studentWorksheetId && commonRepo.studentAppSetting) {
      service.loadAndSetStudentWorksheetScorings(+studentWorksheetId)
    }
  }, [isParentView, studentWorksheetId, JSON.stringify(commonRepo.studentAppSetting)])

  // 최초 진입(reload)시 비공개 학습지일 경우 이전 페이지로 돌아감.
  useEffect(() => {
    if (service.worksheet?.accessModifierToStudent === 'PRIVATE') {
      navigate(-1)
    }
  }, [service.worksheet?.accessModifierToStudent])

  useEffect(() => {
    if (!studentWorksheetId) {
      navigate(routeName.student.studentWorksheet)
    }
  }, [studentWorksheetId])

  const onSubmitProblems = () => {
    if (service.worksheet?.type === WorksheetDomain.TYPE.오늘의학습) {
      mixpanelClient.track('오늘의학습_제출하기버튼_Click', {
        난이도: ProblemEntity.Problem.getKoLevel(service.worksheet?.level),
      })
    }
    if (service.problemScoringColl?.toScoredArr.length) {
      modalService.openModal(
        {
          content: (
            <ScoringSubmitConfirmModal
              onSubmit={async () => {
                if (!studentWorksheetId) {
                  return
                }
                try {
                  modalService.showLoader()
                  modalService.closeModal()
                  if (service.worksheet?.type === WorksheetDomain.TYPE.오늘의학습) {
                    await service.onSubmitDailyWorksheetProblems(studentWorksheetId)
                    mixpanelClient.track('오늘의학습_최종제출하기버튼_Click', {
                      난이도: ProblemEntity.Problem.getKoLevel(service.worksheet?.level),
                    })
                  } else {
                    await service.onSubmitWorksheetProblems(studentWorksheetId)
                  }
                } catch (error) {
                  if (error instanceof Error) {
                    // CASE: 학습지가 삭제되거나 수정된 경우
                    if (
                      error.message === 'WORKSHEET_ALREADY_DELETED' ||
                      error.message === 'STUDENT_WORKSHEET_NOT_FOUND'
                    ) {
                      alertWorksheetExceptionErrorModal()
                    } else if (error.message === 'WORKSHEET_PERMISSION_DENIED') {
                      openUnavailableAlertMessageModal()
                    } else {
                      // CASE: 학습지에 대한 접근 권한이 없을 때
                      openUnavailableAlertMessageModal()
                    }
                  }
                } finally {
                  modalService.hideLoader()
                }
              }}
            />
          ),
        },
        {
          modalName: 'confirmToSubmitAnswers',
        },
      )
    }
  }

  const onVirtualScoringBatchSubmit = useCallback(async () => {
    // 전송 스냅샷 생성 (전송 중 추가 입력 보존)
    const snapshot = Array.from(tempLocalUserInputRef.current.entries())

    if (snapshot.length === 0) return

    const idNum = Number(studentWorksheetId)

    if (!studentWorksheetId || Number.isNaN(idNum)) return

    try {
      const payload = snapshot.map(([, v]) => v)

      await service.onVirtualScoringSubmitById(idNum, payload)

      // 전송 당시 값과 동일한 항목만 제거 (전송 중 수정된 값은 보존)
      for (const [k, sent] of snapshot) {
        const cur = tempLocalUserInputRef.current.get(k)
        if (cur && JSON.stringify(cur) !== JSON.stringify(sent)) continue
        tempLocalUserInputRef.current.delete(k)
      }
    } catch (error) {
      console.error('Virtual scoring submit failed:', error)
    }
  }, [service, studentWorksheetId])

  const onDebouncedVirtualScoringBatchSubmit = useCallback(
    debounce(onVirtualScoringBatchSubmit, 1000),
    [onVirtualScoringBatchSubmit],
  )

  const _onVirtualScoringSubmit = (
    localUserInput: RequestScoring.자동채점<'WORKSHEET'> | RequestScoring.일반채점<'WORKSHEET'>,
  ) => {
    // 차단하지 않으면 언마운트 시 onVirtualScoringSubmitById 가 localStorage 답안을 삭제해 유실됨.
    if (service.scoringSaveStorage !== 'SERVER') return
    if (!service.worksheet?.autoScorable || !(localUserInput instanceof RequestScoring.자동채점)) {
      return
    }
    const params = RequestScoring.자동채점.toRequestParams<'WORKSHEET', '일반'>(localUserInput)

    tempLocalUserInputRef.current.set(params.worksheetProblemId, params)
    onDebouncedVirtualScoringBatchSubmit()
  }

  useEffect(() => {
    return () => {
      // 컴포넌트 언마운트 시 debounced 함수 취소하고 남은 데이터 즉시 전송
      onDebouncedVirtualScoringBatchSubmit.cancel()
      if (tempLocalUserInputRef.current.size > 0) {
        onVirtualScoringBatchSubmit()
      }
    }
  }, [onDebouncedVirtualScoringBatchSubmit, onVirtualScoringBatchSubmit])

  if (!studentWorksheetId) return null

  const lastScreenProblemIndex = learningContentLastScreenService.getLastScreenProblemIndex([
    'STUDENT_WORKSHEET',
    +studentWorksheetId,
  ])

  const updateLastScreenProblemIndex = (currentProblemIndex: number) => {
    if (studentWorksheetId) {
      learningContentLastScreenService.setLastScreenProblemIndex(
        ['STUDENT_WORKSHEET', +studentWorksheetId],
        currentProblemIndex,
      )
    }
  }

  return (
    <>
      <StudentWorksheetHeader title={service.worksheet?.title} />
      {isMobile ? (
        <WorksheetScoringByOneMobile
          problemScoringColl={service.problemScoringColl}
          viewOption={service.problemScoringViewOption}
          onSubmit={onSubmitProblems}
          studentWorksheet={service.studentWorksheet}
          type={service.worksheet?.type}
          problemIndex={lastScreenProblemIndex}
          onProblemIndexChange={updateLastScreenProblemIndex}
          onLocalUserInputChange={_onVirtualScoringSubmit}
        />
      ) : (
        <WorksheetScoringByOne
          problemScoringColl={service.problemScoringColl}
          viewOption={service.problemScoringViewOption}
          onSubmit={onSubmitProblems}
          studentWorksheet={service.studentWorksheet}
          worksheet={service.worksheet}
          problemIndex={lastScreenProblemIndex}
          onProblemIndexChange={updateLastScreenProblemIndex}
          onLocalUserInputChange={_onVirtualScoringSubmit}
        />
      )}
    </>
  )
})

export default StudentWorksheetDetailPage
